import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Image, Spin, Typography } from 'antd';
import type { ImageProps } from 'antd';
import { fetchProtectedObjectUrl } from '../api/client';

/** 后端受 JWT 保护的上传目录（静态访问在 main.ts 中间件里被拦截） */
const PROTECTED_PREFIXES = ['/uploads/id-cards/', '/uploads/payment-vouchers/'];

export function isProtectedUpload(url: string): boolean {
  return PROTECTED_PREFIXES.some((prefix) => url.includes(prefix));
}

interface ProtectedImageProps {
  /** 后端返回的相对路径（/uploads/...）或绝对 URL；空值渲染占位文案 */
  src?: string | null;
  width?: number;
  height?: number;
  /** 加载失败时的兜底图（透传 antd Image） */
  fallback?: string;
  /** 透传 antd Image 的预览配置（如自定义 mask） */
  preview?: ImageProps['preview'];
  emptyText?: string;
  style?: CSSProperties;
}

/**
 * 受保护上传文件的预览组件。
 *
 * 身份证照片与付款凭证的静态目录只认 Authorization 请求头，`?token=` 已被后端移除，
 * 所以必须先带令牌把文件拉成 Blob，再用 object URL 交给 <img>——令牌不会出现在
 * URL、访问日志或浏览器历史里。object URL 在卸载或 src 变化时立即释放。
 */
export function ProtectedImage({
  src,
  width = 44,
  height = 32,
  fallback,
  preview,
  emptyText = '未上传',
  style,
}: ProtectedImageProps) {
  const protectedSrc = src && isProtectedUpload(src) ? src : null;
  const [objectUrl, setObjectUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!protectedSrc) return;
    let settled = false;
    let created: string | null = null;
    setFailed(false);
    fetchProtectedObjectUrl(protectedSrc)
      .then((blobUrl) => {
        // 请求返回时组件已卸载或 src 已变更：直接释放，避免泄漏
        if (settled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        created = blobUrl;
        setObjectUrl(blobUrl);
      })
      .catch(() => {
        if (!settled) setFailed(true);
      });
    return () => {
      settled = true;
      if (created) URL.revokeObjectURL(created);
      setObjectUrl('');
    };
  }, [protectedSrc]);

  if (!src) return <Typography.Text type="secondary">{emptyText}</Typography.Text>;

  // 非受保护路径（外链等）直接渲染
  if (!protectedSrc) {
    return <Image src={src} width={width} height={height} style={style} preview={preview} fallback={fallback} />;
  }

  if (failed) return <Typography.Text type="secondary">无法加载</Typography.Text>;
  if (!objectUrl) return <Spin size="small" />;

  return <Image src={objectUrl} width={width} height={height} style={style} preview={preview} fallback={fallback} />;
}
